export type IdleTimerEventName = 'idle' | 'exitidle';

/**
 * Simple activity-based idle timer.
 *
 * - Call `reset()` on any activity.
 * - Emits `idle` after `waitMs` without a reset.
 * - Emits `exitidle` on the first reset after becoming idle.
 *
 * Notes:
 * - `reset()` is intentionally O(1) and does not churn timers.
 * - A lightweight interval checks for idleness.
 */
export class IdleTimer extends EventTarget {
  private waitMs: number;
  private lastActivityMs: number;
  private isIdle = false;
  private isPaused = false;
  private intervalId: number | null = null;
  private readonly checkIntervalMs: number;

  constructor(opts?: { waitMs?: number; checkIntervalMs?: number }) {
    super();
    this.waitMs = opts?.waitMs ?? 30_000;
    this.checkIntervalMs = opts?.checkIntervalMs ?? 250;
    this.lastActivityMs = Date.now();
    this.startWatcher();
  }

  setWaitMs = (ms: number) => {
    this.waitMs = Math.max(0, ms);
  };

  markIdle = () => {
    this.isIdle = true;
    this.dispatchEvent(new Event('idle'));
  };

  reset = () => {
    this.lastActivityMs = Date.now();
    if (this.isIdle) {
      this.isIdle = false;
      this.dispatchEvent(new Event('exitidle'));
    }
  };

  pause = () => {
    this.isPaused = true;
    this.stopWatcher();
  };

  restart = () => {
    this.isPaused = false;
    this.isIdle = false;
    this.lastActivityMs = Date.now();
    this.startWatcher();
  };

  dispose = () => {
    this.stopWatcher();
  };

  private startWatcher() {
    if (this.isPaused) return;
    if (this.intervalId !== null) return;

    this.intervalId = window.setInterval(() => {
      if (this.isPaused) return;
      if (this.isIdle) return;

      const now = Date.now();
      if (now - this.lastActivityMs >= this.waitMs) {
        this.isIdle = true;
        this.dispatchEvent(new Event('idle'));
      }
    }, this.checkIntervalMs);
  }

  private stopWatcher() {
    if (this.intervalId === null) return;
    window.clearInterval(this.intervalId);
    this.intervalId = null;
  }
}
